import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Stack,
  IconButton
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
  LineChart,
  Line
} from "recharts";
import api from "../services/api";

interface AnalyticsData {
  users: { total: number; verified: number; unverified: number; recent: number };
  challans: { total: number; paid: number; unpaid: number; recent: number };
  scholarships: { total: number; pending: number; approved: number; rejected: number; recent: number };
  revenue: { total: number; pending: number; collected: number };
}

export default function Dashboard() {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [collectionData, setCollectionData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [chartTab, setChartTab] = useState('MONTHLY');
  const [collectionTab, setCollectionTab] = useState('30 Days');

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await api.get("/analytics/dashboard");
        setAnalytics(response.data.data);
      } catch (err) {
        console.error("Error fetching analytics:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const days = chartTab === 'DAILY' ? 1 : chartTab === 'WEEKLY' ? 7 : chartTab === 'YEARLY' ? 365 : 30;
        const [revRes, userRes] = await Promise.all([
          api.get(`/analytics/charts/revenue?days=${days}`),
          api.get(`/analytics/charts/users?days=${days}`)
        ]);
        
        const revStats = revRes.data.data.revenueStats || [];
        const usrStats = userRes.data.data.userStats || [];
        
        const mergedMap = new Map();
        revStats.forEach((r: any) => {
          const dateKey = `${r._id.year}-${String(r._id.month).padStart(2,'0')}-${String(r._id.day).padStart(2,'0')}`;
          mergedMap.set(dateKey, { name: dateKey, revenue: r.revenue || 0, users: 0 });
        });
        usrStats.forEach((u: any) => {
          const dateKey = `${u._id.year}-${String(u._id.month).padStart(2,'0')}-${String(u._id.day).padStart(2,'0')}`;
          if (mergedMap.has(dateKey)) {
            mergedMap.get(dateKey).users = u.count || 0;
          } else {
            mergedMap.set(dateKey, { name: dateKey, revenue: 0, users: u.count || 0 });
          }
        });
        
        let finalData = Array.from(mergedMap.values()).sort((a: any, b: any) => new Date(a.name).getTime() - new Date(b.name).getTime());
        if (finalData.length === 0) {
            finalData = [{ name: 'No Data', revenue: 0, users: 0 }, { name: 'Today', revenue: 0, users: 0 }];
        }
        setChartData(finalData);
      } catch (err) {
        console.error("Error fetching chart data:", err);
      }
    };
    fetchChartData();
  }, [chartTab]);

  useEffect(() => {
    const fetchCollectionData = async () => {
      try {
        const days = collectionTab === '7 Days' ? 7 : collectionTab === '30 Days' ? 30 : collectionTab === '12 Months' ? 365 : 3650;
        const [revRes, userRes] = await Promise.all([
          api.get(`/analytics/charts/revenue?days=${days}`),
          api.get(`/analytics/charts/users?days=${days}`)
        ]);
        
        const revStats = revRes.data.data.revenueStats || [];
        const usrStats = userRes.data.data.userStats || [];
        
        const mergedMap = new Map();
        revStats.forEach((r: any) => {
          const dateKey = `${r._id.year}-${String(r._id.month).padStart(2,'0')}-${String(r._id.day).padStart(2,'0')}`;
          mergedMap.set(dateKey, { name: dateKey, revenue: r.revenue || 0, users: 0 });
        });
        usrStats.forEach((u: any) => {
          const dateKey = `${u._id.year}-${String(u._id.month).padStart(2,'0')}-${String(u._id.day).padStart(2,'0')}`;
          if (mergedMap.has(dateKey)) {
            mergedMap.get(dateKey).users = u.count || 0;
          } else {
            mergedMap.set(dateKey, { name: dateKey, revenue: 0, users: u.count || 0 });
          }
        });
        
        let finalData = Array.from(mergedMap.values()).sort((a: any, b: any) => new Date(a.name).getTime() - new Date(b.name).getTime());
        if (finalData.length === 0) {
            finalData = [{ name: 'No Data', revenue: 0, users: 0 }, { name: 'Today', revenue: 0, users: 0 }];
        }
        setCollectionData(finalData);
      } catch (err) {
        console.error("Error fetching collection data:", err);
      }
    };
    fetchCollectionData();
  }, [collectionTab]);

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#4318ff' }} />
      </Box>
    );
  }

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-PK", { style: "currency", currency: "PKR", minimumFractionDigits: 0 }).format(amount);

  const hsData = [
    { name: "Approved", value: analytics?.scholarships?.approved || 0 },
    { name: "Pending", value: analytics?.scholarships?.pending || 0 },
    { name: "Rejected", value: analytics?.scholarships?.rejected || 0 },
    { name: "Total", value: analytics?.scholarships?.total || 0 }
  ];
  const hsColors = ["#8c54ff", "#ffb300", "#4caf50", "#2196f3"];

  return (
    <Box sx={{ bgcolor: '#f4f7fe', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: '1440px', margin: '0 auto' }}>
        <Grid container spacing={3}>
          
          {/* ======================= TOP ROW ======================= */}
          <Grid size={{ xs: 12, lg: 9 }}>
            <Box sx={{ bgcolor: '#fff', borderRadius: '20px', p: 3, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
              <Box sx={{ flex: 1, borderRight: { md: '1px solid #eee' }, pr: { md: 4 }, mb: { xs: 3, md: 0 } }}>
                <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#2b3674', mb: 4 }}>Platform Overview</Typography>
                <Typography sx={{ color: '#a3aed1', fontSize: '12px' }}>Total Revenue</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '30px', color: '#2b3674' }}>
                    {formatCurrency(analytics?.revenue?.total || 0)}
                  </Typography>
                  <Box sx={{ bgcolor: '#e6f8ec', color: '#05cd99', px: 1, py: 0.5, borderRadius: '4px', fontSize: '10px', fontWeight: 700 }}>+0.25%</Box>
                </Box>
              </Box>

              <Box sx={{ flex: 2, pl: { md: 4 }, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                {[
                  { title: 'Total Users', val: analytics?.users?.total || 0, icon: 'U', bg: '#05cd99', code: 'All Platform Users', change: '+15%' },
                  { title: 'Verified Users', val: analytics?.users?.verified || 0, icon: 'V', bg: '#4318ff', code: 'Active Users', change: '+3%' },
                  { title: 'Total Challans', val: analytics?.challans?.total || 0, icon: 'C', bg: '#ffb547', code: 'Generated Challans', change: '+3%' },
                  { title: 'Paid Challans', val: analytics?.challans?.paid || 0, icon: 'P', bg: '#868cff', code: 'Completed Payments', change: '+3%' },
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold' }}>
                      {item.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#2b3674' }}>{item.title}</Typography>
                      <Typography sx={{ fontSize: '10px', color: '#a3aed1' }}>{item.code}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#2b3674' }}>{item.val}</Typography>
                      <Typography sx={{ fontSize: '10px', color: item.change.includes('+') ? '#05cd99' : '#e31a1a' }}>{item.change}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, lg: 3 }}>
            <Box sx={{ bgcolor: '#fff', borderRadius: '20px', p: 3, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#f4f7fe', color: '#4318ff', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>Rs</Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '20px', color: '#2b3674' }}>{formatCurrency(analytics?.revenue?.collected || 0)}</Typography>
                  <Typography sx={{ fontSize: '11px', color: '#a3aed1' }}>Revenue data</Typography>
                </Box>
                <Box sx={{ height: 60, width: '60%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData.slice(-7)}>
                      <Bar dataKey="revenue" fill="#4318ff" radius={[4, 4, 0, 0]} barSize={4} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* ======================= MIDDLE ROW ======================= */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Box sx={{ bgcolor: '#fff', borderRadius: '20px', p: 3, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#2b3674' }}>Revenue Analytics</Typography>
                  <Typography sx={{ fontSize: '12px', color: '#a3aed1', mt: 0.5 }}>Overview of growth</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  {['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'].map(tab => (
                    <Typography 
                      key={tab} onClick={() => setChartTab(tab)}
                      sx={{ fontSize: '11px', fontWeight: 600, color: chartTab === tab ? '#4318ff' : '#a3aed1', bgcolor: chartTab === tab ? '#f4f7fe' : 'transparent', px: 1.5, py: 0.5, borderRadius: '6px', cursor: 'pointer' }}
                    >
                      {tab}
                    </Typography>
                  ))}
                </Stack>
              </Box>
              
              <Box sx={{ display: 'flex', flex: 1 }}>
                <Box sx={{ flex: 1, position: 'relative' }}>
                  <Box sx={{ position: 'absolute', top: 10, left: 40, display: 'flex', gap: 3, zIndex: 10 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#05cd99', fontSize: '11px', fontWeight: 700 }}>
                        <span style={{ fontSize: '16px' }}>↑</span> +3.49%
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '20px', color: '#2b3674' }}>{analytics?.revenue?.total || 0}</Typography>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#05cd99', fontSize: '11px', fontWeight: 700 }}>
                        <span style={{ fontSize: '16px' }}>↑</span> +1.42%
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '20px', color: '#2b3674' }}>{analytics?.users?.total || 0}</Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ height: '100%', minHeight: 250, width: '100%', ml: -2 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 70, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4318ff" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="#4318ff" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#a3aed1' }} dy={10} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#a3aed1' }} dx={-10} />
                        <Tooltip />
                        <Area type="monotone" dataKey="revenue" stroke="#4318ff" strokeWidth={3} fillOpacity={1} fill="url(#colorRev)" />
                        <Area type="monotone" dataKey="users" stroke="#05cd99" strokeWidth={3} fill="none" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
                
                <Box sx={{ width: '200px', borderLeft: '1px solid #eee', pl: 3, pt: 3, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Box>
                    <Typography sx={{ fontSize: '12px', color: '#a3aed1', mb: 0.5 }}>Recent Revenue</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '20px', color: '#2b3674' }}>{formatCurrency(analytics?.revenue?.pending || 0)} <span style={{fontSize: '10px', color: '#a3aed1', fontWeight: 500}}>/ Working</span></Typography>
                    <Typography sx={{ fontSize: '10px', color: '#05cd99', mt: 0.5, fontWeight: 700 }}>↑ 11.2% <span style={{color: '#a3aed1', fontWeight: 500}}>Trend</span></Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '12px', color: '#a3aed1', mb: 0.5 }}>Recent Users</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '20px', color: '#2b3674' }}>{analytics?.users?.recent || 0} <span style={{fontSize: '10px', color: '#a3aed1', fontWeight: 500}}>/ Last 30 Days</span></Typography>
                    <Typography sx={{ fontSize: '10px', color: '#05cd99', mt: 0.5, fontWeight: 700 }}>↑ 11.2% <span style={{color: '#a3aed1', fontWeight: 500}}>Trend</span></Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <Box sx={{ bgcolor: '#fff', borderRadius: '20px', p: 3, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#2b3674' }}>Pending Revenue</Typography>
                  <Typography sx={{ fontSize: '12px', color: '#a3aed1' }}>Working Balance</Typography>
                </Box>
                <Typography sx={{ fontSize: '12px', color: '#a3aed1' }}>Currency <span style={{fontWeight: 700, color: '#2b3674'}}>PKR</span></Typography>
              </Box>

              <Box sx={{ position: 'relative', mb: 3 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '26px', color: '#2b3674' }}>{formatCurrency(analytics?.revenue?.pending || 0)}</Typography>
                <Box sx={{ height: 120, width: '100%', ml: -2, mt: 1 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData.slice(-7)}>
                      <defs>
                        <linearGradient id="colorGreen" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#05cd99" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#05cd99" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="name" hide />
                      <YAxis hide />
                      <Area type="monotone" dataKey="revenue" stroke="#05cd99" strokeWidth={2} fillOpacity={1} fill="url(#colorGreen)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Box>

              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#2b3674', mb: 2 }}>Scholarship Status</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  { name: 'Approved', val: analytics?.scholarships?.approved || 0, bg: '#05cd99' },
                  { name: 'Pending', val: analytics?.scholarships?.pending || 0, bg: '#ffb547' },
                  { name: 'Rejected', val: analytics?.scholarships?.rejected || 0, bg: '#4318ff' },
                  { name: 'Total', val: analytics?.scholarships?.total || 0, bg: '#868cff' }
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{item.name[0]}</Box>
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#2b3674' }}>{item.name}</Typography>
                        <Typography sx={{ fontSize: '10px', color: '#a3aed1' }}>Count of HS Cards</Typography>
                      </Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#2b3674' }}>{item.val}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* ======================= BOTTOM ROW ======================= */}
          {/* Left Column in Bottom Row */}
          <Grid size={{ xs: 12, md: 4, lg: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
              <Box sx={{ bgcolor: '#fff', borderRadius: '20px', p: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#2b3674', mb: 2 }}>HS Card Status</Typography>
                <Box sx={{ height: 160, display: 'flex', justifyContent: 'center' }}>
                  <PieChart width={200} height={160}>
                    <Pie data={hsData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" stroke="none">
                      {hsData.map((_, i) => <Cell key={i} fill={hsColors[i]} />)}
                    </Pie>
                  </PieChart>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5, mt: 1 }}>
                   {hsData.map((item, i) => (
                     <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                       <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: hsColors[i] }} />
                       <Typography sx={{ fontSize: '10px', color: '#a3aed1' }}>{item.name}</Typography>
                     </Box>
                   ))}
                </Box>
              </Box>

              <Box sx={{ bgcolor: '#fff', borderRadius: '20px', p: 3, flex: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#2b3674', mb: 2 }}>Platform Usage</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '20px', color: '#2b3674' }}>{Math.round(((analytics?.users?.verified || 0) / (analytics?.users?.total || 1)) * 100)}%</Typography>
                    <Typography sx={{ fontSize: '10px', color: '#a3aed1' }}>Verified</Typography>
                  </Box>
                  <Box sx={{ position: 'relative', width: 60, height: 60 }}>
                    <CircularProgress variant="determinate" value={100} size={60} thickness={5} sx={{ color: '#f4f7fe' }} />
                    <CircularProgress variant="determinate" value={84} size={60} thickness={5} sx={{ color: '#4318ff', position: 'absolute', left: 0 }} />
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    { label: 'Verified Users', val: analytics?.users?.verified, color: '#05cd99' },
                    { label: 'Paid Challans', val: analytics?.challans?.paid, color: '#ffb547' },
                    { label: 'Approved Sch', val: analytics?.scholarships?.approved, color: '#4318ff' },
                    { label: 'Pending Sch', val: analytics?.scholarships?.pending, color: '#ff3b3b' },
                  ].map((item, i) => (
                    <Box key={i}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '10px', color: '#2b3674', fontWeight: 600 }}>{item.label}</Typography>
                        <Typography sx={{ fontSize: '10px', color: '#a3aed1' }}>{item.val}</Typography>
                      </Box>
                      <Box sx={{ width: '100%', height: 4, bgcolor: '#f4f7fe', borderRadius: 2 }}>
                        <Box sx={{ width: '70%', height: '100%', bgcolor: item.color, borderRadius: 2 }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Middle Column in Bottom Row */}
          <Grid size={{ xs: 12, md: 4, lg: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
              <Box sx={{ bgcolor: '#fff', borderRadius: '20px', p: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#2b3674' }}>Growth Analytics</Typography>
                </Box>
                <Box sx={{ height: 150, width: '100%', ml: -2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData.slice(-7)}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#a3aed1' }} dy={10} />
                      <YAxis hide />
                      <Line type="monotone" dataKey="revenue" stroke="#4318ff" strokeWidth={3} dot={{r:4, fill:'#4318ff'}} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>

              <Box sx={{ bgcolor: '#fff', borderRadius: '20px', p: 3, flex: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#2b3674', mb: 3 }}>Data Summary (Daily Revenue)</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {chartData.slice(-6).map((item, i) => {
                    const maxRev = Math.max(...chartData.map(d => d.revenue || 1));
                    const width = `${Math.max(10, ((item.revenue || 0) / maxRev) * 100)}%`;
                    return (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ fontSize: '10px', color: '#a3aed1', width: 60, whiteSpace: 'nowrap', overflow: 'hidden' }}>{item.name}</Typography>
                      <Box sx={{ flex: 1, height: 8, bgcolor: '#f4f7fe', borderRadius: 4, position: 'relative' }}>
                        <Box sx={{ position: 'absolute', left: 0, top: 0, height: '100%', width: width, bgcolor: ['#4318ff', '#05cd99', '#ffb547', '#ff3b3b'][i%4], borderRadius: 4 }} />
                      </Box>
                      <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#2b3674', width: 40, textAlign: 'right' }}>Rs {item.revenue}</Typography>
                    </Box>
                  )})}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right Column in Bottom Row */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
              <Box sx={{ bgcolor: '#fff', borderRadius: '20px', p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#2b3674', mb: 1 }}>Company Balance</Typography>
                  <Typography sx={{ fontSize: '11px', color: '#a3aed1' }}>Total Balance</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '24px', color: '#2b3674' }}>{formatCurrency(analytics?.revenue?.total || 0)}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '11px', color: '#a3aed1' }}>Collected</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#2b3674' }}>{formatCurrency(analytics?.revenue?.collected || 0)}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '11px', color: '#a3aed1' }}>Pending</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#2b3674' }}>{formatCurrency(analytics?.revenue?.pending || 0)}</Typography>
                </Box>
              </Box>

              <Box sx={{ bgcolor: '#fff', borderRadius: '20px', p: 3, flex: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#2b3674' }}>Monthly Collections</Typography>
                  <Stack direction="row" spacing={1}>
                    {['All Date', '12 Months', '30 Days', '7 Days'].map(tab => (
                      <Typography 
                        key={tab} onClick={() => setCollectionTab(tab)}
                        sx={{ fontSize: '10px', fontWeight: 600, color: collectionTab === tab ? '#4318ff' : '#a3aed1', bgcolor: collectionTab === tab ? '#f4f7fe' : 'transparent', px: 1, py: 0.5, borderRadius: '4px', cursor: 'pointer' }}
                      >
                        {tab}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
                
                <Box sx={{ flex: 1, minHeight: 250, width: '100%', ml: -2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={collectionData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#a3aed1' }} dy={10} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#a3aed1' }} dx={-10} />
                      <Tooltip />
                      <Bar dataKey="revenue" fill="#4318ff" radius={[4, 4, 0, 0]} barSize={15} />
                      <Bar dataKey="users" fill="#05cd99" radius={[4, 4, 0, 0]} barSize={15} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </Box>
          </Grid>

        </Grid>
      </Box>
    </Box>
  );
}
